/**
 * The report's Tulevaisuustestaus v2 sections (#1474, specs/workshop-modules/future_reimagined.md,
 * "v2"), v1's content (ReportFutureSections) with vipupisteet in place of metrics:
 *   future-interplay-v2  how the solutions affect each other, and through which vipupisteet
 *   future-timeline-v2   the locked set's course in each scenario (fates, the effect verdict, the
 *                        first moments), which vipupisteet the set still moves, and which
 *                        solutions hold in every scenario
 * The data is the module's state (useChainReportData), without this device's votes.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Section, SectionTitle, SectionSubtitle, Paragraph, SITRA } from './reportStyles';
import { robustness } from '../../modules/future_reimagined_v2/robustness';
import { worldTabs, tabLabel } from '../../modules/future_reimagined_v2/worldTabs';
import { vipupisteOutcomes } from '../../modules/future_reimagined_v2/vipupisteOutcomes';

const RELATION_ACCENT = { conflict: '#c0392b', overlap: '#d4a017', competing: '#e07b2a', enables: '#2f6fb3', reinforces: '#2e9e5b' };
const FATE_ACCENT = { realised: '#2e9e5b', delayed: '#2f6fb3', diminished: '#d4a017', failed: '#c0392b' };
/** Moments per scenario in the report: the narrative's first few, enough to show the course's character */
const REPORT_MOMENTS = 4;

const List = styled.ul`margin: 12px 0 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px;`;
const Item = styled.li`
  padding: 12px 14px; border-radius: 4px; border: 1px solid ${SITRA.border};
  border-left: 4px solid ${(p) => p.$accent || SITRA.border}; background: ${SITRA.white}; color: ${SITRA.textBody}; line-height: 1.55;
  .title { font-weight: 600; }
  .meta { margin-top: 4px; font-size: 14px; color: ${SITRA.textMuted}; }
  p { margin: 6px 0 0; }
`;
const Grid = styled.table`
  width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 14px;
  th, td { text-align: left; padding: 8px; border-bottom: 1px solid ${SITRA.border}; vertical-align: top; }
  th { color: ${SITRA.textMuted}; font-weight: 600; }
`;

/** Which of the sections have something to show */
export function futureV2Sections(state) {
  const has = new Set();
  if (state?.interplay?.edges?.some((e) => e.relation !== 'independent')) has.add('future-interplay-v2');
  if (state?.run && state?.timelines?.some((tl) => tl.status === 'done')) has.add('future-timeline-v2');
  return has;
}

export function ReportFutureInterplayV2({ state }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`report.chainSections.futureV2.${key}`, vars);
  const title = new Map(state.solutions.map((s) => [s.id, s.title]));
  const vipupiste = new Map(state.vipupisteet.map((v) => [v.id, v.title]));
  const shown = state.interplay.edges.filter((e) => e.relation !== 'independent');
  const independent = state.interplay.edges.length - shown.length;
  return (
    <Section id="future-interplay-v2">
      <SectionTitle>{t('report.nav.futureInterplayV2')}</SectionTitle>
      <Paragraph>{k('interplayIntro')}</Paragraph>
      <List>
        {shown.map((e) => {
          const [a, b] = e.relation === 'enables' && e.from === e.b ? [e.b, e.a] : [e.a, e.b];
          const via = (e.viaVipupisteIds || []).map((id) => vipupiste.get(id)).filter(Boolean);
          return (
            <Item key={`${e.a}:${e.b}`} $accent={RELATION_ACCENT[e.relation]}>
              <div className="title">{k(`relation.${e.relation}`)}: {title.get(a)} {e.relation === 'enables' ? '→' : '↔'} {title.get(b)}</div>
              <p>{e.explanation}</p>
              {via.length > 0 && <div className="meta">{k('via', { list: via.join(' · ') })}</div>}
              {e.uncertainty && <div className="meta">{k('uncertainty', { text: e.uncertainty })}</div>}
            </Item>
          );
        })}
      </List>
      {independent > 0 && <Paragraph>{k('independent', { count: independent })}</Paragraph>}
    </Section>
  );
}

export function ReportFutureTimelineV2({ state }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`report.chainSections.futureV2.${key}`, vars);
  const title = (id) => state.solutions.find((s) => s.id === id)?.title || k('deleted');
  const tabs = worldTabs(state.timelines).filter((tab) => tab.timeline.status === 'done');
  const runs = tabs.map((tab) => tab.timeline);
  const locked = new Set(state.run.solutionIds);
  const outcomes = vipupisteOutcomes(state.vipupisteet, state.solutions.filter((s) => locked.has(s.id)), runs);
  return (
    <Section id="future-timeline-v2">
      <SectionTitle>{t('report.nav.futureTimelineV2')}</SectionTitle>
      <Paragraph>{k('timelineIntro')}</Paragraph>
      <Paragraph>{k('locked', { names: state.run.solutionIds.map(title).join('; ') })}</Paragraph>
      {state.run.overrideReason && <Paragraph>{k('override', { reason: state.run.overrideReason })}</Paragraph>}

      {tabs.map((tab) => {
        const tl = tab.timeline;
        const verdict = tl.effect?.set?.verdict;
        return (
          <React.Fragment key={tab.id}>
            <SectionSubtitle>{k('scenario', { name: tabLabel(tab, 80) })}</SectionSubtitle>
            {verdict && <Paragraph>{k('effect', { verdict: k(`verdict.${verdict}`) })}{tl.effect.set.why ? `: ${tl.effect.set.why}` : ''}</Paragraph>}
            <List>
              {tl.fates.map((f) => (
                <Item key={f.solutionId} $accent={FATE_ACCENT[f.fate]}>
                  <div className="title">{title(f.solutionId)}: {k(`fate.${f.fate}`)} {f.year ?? ''}</div>
                  <p>{f.why}</p>
                  {f.whatWouldChange && <div className="meta">{k('wouldChange', { text: f.whatWouldChange })}</div>}
                </Item>
              ))}
            </List>
            {tl.narrative?.length > 0 && (
              <List>
                {tl.narrative.slice(0, REPORT_MOMENTS).map((p, i) => (
                  <Item key={i}>
                    <div className="title">{p.year} · {p.title}</div>
                    <p>{p.narrative}</p>
                    <div className="meta">{k('uncertainty', { text: p.uncertainty })}</div>
                  </Item>
                ))}
              </List>
            )}
          </React.Fragment>
        );
      })}

      {outcomes.length > 0 && (
        <>
          <SectionSubtitle>{k('vipupisteetTitle')}</SectionSubtitle>
          <List>
            {outcomes.map((o) => <Item key={o.vipupiste.id}>{k('holds', { name: o.vipupiste.title, n: o.holds, total: o.total })}</Item>)}
          </List>
        </>
      )}

      {runs.length > 1 && (
        <>
          <SectionSubtitle>{k('robustnessTitle')}</SectionSubtitle>
          <Grid>
            <thead>
              <tr><th aria-hidden />{tabs.map((tab) => <th key={tab.id}>{tabLabel(tab)}</th>)}<th>{k('robustnessVerdict')}</th></tr>
            </thead>
            <tbody>
              {robustness(state.run.solutionIds, runs).map((r) => (
                <tr key={r.solutionId}>
                  <th scope="row">{title(r.solutionId)}</th>
                  {r.cells.map((c) => (
                    <td key={c.timelineId} style={{ color: c.fate ? FATE_ACCENT[c.fate.fate] : SITRA.textMuted }}>
                      {c.fate ? `${k(`fate.${c.fate.fate}`)} ${c.fate.year ?? ''}` : '—'}
                    </td>
                  ))}
                  <td>{k(`robustness.${r.verdict}`)}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </>
      )}
    </Section>
  );
}
